import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { ExportDataButton } from 'sharedUi/ExportDataButton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useDebounce } from '../../hooks/useDebounce';
import { valueSetsService, type ValueSetQueryParams } from '../../services/value-sets.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { extractErrorMessage } from '../../utils/error-handler';
import type { ValueSet } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { formatDate } from 'sharedUi/dateUtils';

// Extended type for DataGrid compatibility
type ValueSetWithId = ValueSet & { id: string };

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'key', header: 'Key', filter: true },
  { field: 'description', header: 'Description', filter: true },
];

const ValueSetsList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const navigate = useAppNavigate();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.VALUE_SETS) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.VALUE_SETS);
  const canDelete = useCanDelete(JOB_NAMES.VALUE_SETS);
  // Authority to mutate CORE (appId=null) rows. Without it, core rows show Edit/Delete
  // disabled with a tooltip — the backend enforces the same rule (assert_can_mutate_core).
  const canManageCore = useHasPermission(PERMISSIONS.CORE_MANAGE);
  const [valueSets, setValueSets] = useState<ValueSetWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedValueSetId, setSelectedValueSetId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showGlobal, setShowGlobal] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  // Show the project filter + "Show core" toggle whenever the caller has at least one
  // project (was > 1). A single-project core admin needs "Show core" too, since all the
  // seeded masters are core (appId:null) and would otherwise be unreachable.
  const canManageProjects = !appId && (accessibleProjects.length >= 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states for server-side sorting
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | undefined>(undefined);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  const fetchValueSets = useCallback(async (
    page: number = 1,
    size: number = pageSize,
    field?: string,
    order?: 1 | -1 | 0,
    activeFilters: Record<string, any> = filters
  ) => {
    try {
      setLoading(true);
      const params: ValueSetQueryParams = {
        page,
        page_size: size,
      };

      if (debouncedSearchQuery) params.search = debouncedSearchQuery;

      // Add sorting parameters (order 0 means unsorted/removed)
      if (field && order && (order as number) !== 0) {
        params.sort_by = field;
        params.sort_order = order === 1 ? 'asc' : 'desc';
      }

      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;

      if (selectedProjectId) params.app_id = selectedProjectId;
      params.include_global = showGlobal;

      const response = await valueSetsService.getAll(params);
      // Map _id to id for DataGrid compatibility
      const mappedData = (response.data || []).map(vs => ({ ...vs, id: vs._id })) as ValueSetWithId[];
      setValueSets(mappedData);

      // Handle both totalItems and total from API response
      const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
      setTotalRecords(total);
    } catch (error) {
      console.error('Failed to fetch value sets:', error);
      showError(extractErrorMessage(error, 'Failed to load value sets'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, selectedProjectId, showGlobal, pageSize, sortField, sortOrder, filters, showError]);

  // Handle page change from DataTable
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchValueSets(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort change from DataTable
  const handleSort = (event: any) => {
    const newField = event.sortField;
    const newOrder = event.sortOrder;
    setSortField(newField);
    setSortOrder(newOrder);
    setFirst(0); // Reset to first page on sort
    fetchValueSets(1, pageSize, newField, newOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchValueSets(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setFirst(0);
    fetchValueSets(1, pageSize, sortField, sortOrder);
  }, [debouncedSearchQuery, selectedProjectId, showGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate using plain key (not encrypted) since value-sets API uses key directly
  const handleView = (id: string | number) => {
    const valueSet = valueSets.find(vs => vs.id === id);
    if (valueSet) {
      navigate(`/value-sets/${valueSet.key}`);
    }
  };

  const handleEdit = (id: string | number) => {
    const valueSet = valueSets.find(vs => vs.id === id);
    if (valueSet) {
      navigate(`/value-sets/${valueSet.key}/edit`);
    }
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedValueSetId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedValueSetId) return;

    try {
      setDeleteLoading(true);
      const valueSet = valueSets.find(vs => vs.id === selectedValueSetId);
      if (valueSet) {
        await valueSetsService.delete(valueSet.key);
        setDeleteDialogOpen(false);
        setSelectedValueSetId(null);
        fetchValueSets(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
      }
    } catch (error: any) {
      console.error('Failed to delete value set:', error);
      setDeleteDialogOpen(false);
      setSelectedValueSetId(null);
      showError(extractErrorMessage(error, 'Failed to delete value set'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedValueSetId(null);
  };

  // Fetch blob for export
  const fetchExportBlob = useCallback(() => valueSetsService.export(), []);

  const columns: SaasColumn<ValueSetWithId>[] = useMemo(() => [
    {
      field: 'key',
      header: 'Key',
      sortable: true,
      filter: true,
      hideable: false,
    },
    {
      field: 'description',
      header: 'Description',
      filter: true,
      body: (rowData) => (
        <span className="whitespace-normal break-words">{rowData.description || '-'}</span>
      ),
    },
    {
      field: 'items',
      header: 'Items Count',
      body: (rowData) => rowData.items?.length || 0,
    },
    {
      field: 'createdAt',
      header: 'Created At',
      sortable: true,
      body: (rowData) => formatDate(rowData.createdAt),
    },
    { field: 'appId', header: 'Project', body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} /> },
    {
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (rowData) => {
        // Core rows (appId=null) are the shared baseline — read-only unless the caller
        // holds CORE.MANAGE. The backend enforces the same rule; this is UX only.
        const isCore = rowData.appId == null;
        const coreReadOnly = isCore && !canManageCore;
        const CORE_TOOLTIP = 'Core record — managed centrally';
        return (
        <div className="flex gap-1 justify-center">
          <button aria-label="View"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={() => handleView(rowData.id)}
            data-testid="ValueSetsList.Action.View"
            data-pr-tooltip="View"
          >
            <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
          </button>

          {canUpdate && (
            <button aria-label="Edit"
              className={`p-2 rounded transition-colors ${coreReadOnly ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
              onClick={() => { if (!coreReadOnly) handleEdit(rowData.id); }}
              aria-disabled={coreReadOnly}
              data-testid="ValueSetsList.Action.Edit"
              data-pr-tooltip={coreReadOnly ? CORE_TOOLTIP : 'Edit'}
            >
              <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.125rem' }} />
            </button>
          )}

          {canDelete && (
            <button aria-label="Delete"
              className={`p-2 rounded transition-colors ${coreReadOnly ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50 dark:hover:bg-red-900/20'}`}
              onClick={() => { if (!coreReadOnly) handleDeleteClick(rowData.id); }}
              aria-disabled={coreReadOnly}
              data-testid="ValueSetsList.Action.Delete"
              data-pr-tooltip={coreReadOnly ? CORE_TOOLTIP : 'Delete'}
            >
              <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
            </button>
          )}
        </div>
        );
      },
    },
  ], [valueSets, canUpdate, canDelete, canManageCore, canManageProjects, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.VALUE_SETS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-ValueSets-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-database"
          title="Value Sets Management"
          subtitle="Manage system value sets and configuration data"
          testId="ValueSets.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="value-sets"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={valueSets}
            loading={loading}
            dataKey="id"
            emptyMessage="No value sets found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchQuery}
            onGlobalSearchChange={setSearchQuery}
            globalSearchPlaceholder="Search value sets..."
            testId="ValueSetsList"
            toolbarStart={
              canManageProjects ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="ValueSetsList.Filter.Project"
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    <InputSwitch
                      checked={showGlobal}
                      onChange={(e: any) => setShowGlobal(!!e.value)}
                      data-testid="ValueSetsList.Toggle.ShowGlobal"
                    />
                    Show core
                  </label>
                </div>
              ) : undefined
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <ExportDataButton
                  fetchBlob={fetchExportBlob}
                  filename="value-sets-export.xlsx"
                  testId="ValueSetsList.Button.Export"
                />
                <RefreshButton
                  onClick={() => fetchValueSets(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                  testId="ValueSetsList.Button.Refresh"
                />
                {canCreate && (
                  <Button
                    label="Create Value Set"
                    icon="pi pi-plus"
                    onClick={() => navigate('/value-sets/create')}
                    testId="ValueSetsList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this value set? This action cannot be undone and may affect system functionality."
          testId="ValueSetsList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default ValueSetsList;
